import { useEffect, useMemo } from "react"

import { useGetTicketingMetadata } from "@/api/useJira"
import { Form } from "@/atoms"
import { TICKET_FIELD_KEYS } from "@/const/ticketing.constants"
import { hasTicketProjects } from "@/domain/ticketing"
import {
  getDisplayMessage,
  getUnavailableVendorMessage
} from "@/helpers/error.helper"
import {
  getProjectTypes,
  getTypeFields,
  pickOptionId,
  toRepositoryProjects
} from "./useVendorTicketingMetadata.selector"

import type { TicketProject } from "@/api/useJira.types"
import type { FormInstance } from "@/atoms"
import type { VendorTicketingSystem } from "@/domain/ticketing"
import type { TicketFormValues } from "@/helpers/ticketing.helpers.types"
import type { VendorTicketingMetadataOptions } from "./useVendorTicketingMetadata.types"

const { project: PROJECT, type: TYPE } = TICKET_FIELD_KEYS
const NO_PROJECTS: TicketProject[] = []

// Projects come first; a project's types and their fields load once it is selected
export const useVendorTicketingMetadata = (
  system: VendorTicketingSystem,
  form: FormInstance<TicketFormValues>,
  options: VendorTicketingMetadataOptions = {}
) => {
  const { initialProjectId, initialTypeId, repository } = options
  const hasProjects = hasTicketProjects(system)
  const metadataContext = useGetTicketingMetadata(
    system,
    {},
    { enabled: hasProjects }
  )
  const { response: metadataResponse, isLoading: metadataIsLoading } =
    metadataContext
  const repositoryProjects = useMemo(
    () => toRepositoryProjects(repository),
    [repository]
  )
  const projects = hasProjects
    ? (metadataResponse?.data?.projects ?? NO_PROJECTS)
    : repositoryProjects

  const projectId = Form.useWatch(PROJECT, form)
  const typeId = Form.useWatch(TYPE, form)

  const fieldsContext = useGetTicketingMetadata(
    system,
    { projectId },
    { enabled: !!projectId }
  )
  const { response: fieldsResponse, isLoading: fieldsIsLoading } = fieldsContext
  const url = metadataResponse?.metadata?.url ?? fieldsResponse?.metadata?.url
  const types = getProjectTypes(fieldsResponse, projectId)
  const fields = getTypeFields(types, typeId)

  useEffect(() => {
    if (projectId || !projects.length) return

    form.setFieldValue(PROJECT, pickOptionId(projects, initialProjectId))
  }, [form, projects, projectId, initialProjectId])

  useEffect(() => {
    if (!types.length || types.some(({ id }) => id === typeId)) return

    form.setFieldValue(TYPE, pickOptionId(types, initialTypeId))
  }, [form, types, typeId, initialTypeId])

  // A vendor may refuse a project's fields with a message for the user (e.g. GitHub
  // missing the Issues permission); nothing can be created there
  const refusalMessage = getDisplayMessage(fieldsContext.error)

  return {
    projectId,
    projects,
    types,
    fields,
    url,
    unavailableMessage:
      getUnavailableVendorMessage(metadataContext.error) ??
      getUnavailableVendorMessage(fieldsContext.error) ??
      refusalMessage,
    isRefused: !!refusalMessage,
    metadataIsLoading,
    fieldsIsLoading: !!projectId && fieldsIsLoading
  }
}
